import React, { useEffect, useState, useCallback } from "react";
import AdminNav from "../components/Admin/AdminNav";
import { API_URL } from "../config";
import { authHeader, getUserId, STAFF_ROLES } from "../auth";
import styles from "./team.module.css";

const ROLE_DESCRIPTIONS = {
  admin: "Full access — products, categories and team management",
  manager: "Can add, edit and delete products and categories",
  viewer: "Read-only access to the backoffice",
};

const EMPTY_FORM = { name: "", email: "", phone: "", password: "", role: "viewer" };

// The API returns JSON on some errors and plain text on others
async function readError(response, fallback) {
  const text = await response.text();
  try {
    return JSON.parse(text).message || fallback;
  } catch {
    return text || fallback;
  }
}

const Team = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const myId = getUserId();

  const fetchMembers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/users`, { headers: authHeader() });
      if (!response.ok) {
        throw new Error(await readError(response, "Could not load the team list."));
      }
      setMembers(await response.json());
      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const flash = (message) => {
    setNotice(message);
    setTimeout(() => setNotice(""), 4000);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const response = await fetch(`${API_URL}/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify(form),
      });
      if (!response.ok) {
        throw new Error(await readError(response, "Could not create the user."));
      }
      setForm(EMPTY_FORM);
      flash(`${form.email} added as ${form.role}.`);
      fetchMembers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (member, role) => {
    setError("");
    try {
      const response = await fetch(`${API_URL}/users/${member._id || member.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ role }),
      });
      if (!response.ok) {
        throw new Error(await readError(response, "Could not change the role."));
      }
      flash(`${member.email} is now ${role}.`);
      fetchMembers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleResetPassword = async (member) => {
    const password = window.prompt(`New password for ${member.email}:`);
    if (!password) return;
    setError("");
    try {
      const response = await fetch(`${API_URL}/users/${member._id || member.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ password }),
      });
      if (!response.ok) {
        throw new Error(await readError(response, "Could not reset the password."));
      }
      flash(`Password reset for ${member.email}.`);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (member) => {
    if (!window.confirm(`Remove ${member.email} from the team? This cannot be undone.`)) {
      return;
    }
    setError("");
    try {
      const response = await fetch(`${API_URL}/users/${member._id || member.id}`, {
        method: "DELETE",
        headers: authHeader(),
      });
      if (!response.ok) {
        throw new Error(await readError(response, "Could not delete the user."));
      }
      flash(`${member.email} removed.`);
      fetchMembers();
    } catch (err) {
      setError(err.message);
    }
  };

  const roleChipClass = (role) => {
    if (role === "admin") return styles.roleChipAdmin;
    if (!role) return styles.roleChipNone;
    return styles.roleChip;
  };

  return (
    <div className={styles.page}>
      <AdminNav />
      <div className={styles.inner}>
        <header className={styles.header}>
          <span className={styles.eyebrow}>Backoffice</span>
          <h1 className={styles.title}>Team</h1>
          <p className={styles.subtitle}>
            Manage who can access the backoffice and what they are allowed to do.
          </p>
        </header>

        {error && <p className={styles.error}>{error}</p>}
        {notice && <p className={styles.success}>{notice}</p>}

        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Members</h2>
          {loading ? (
            <p className={styles.loading}>Loading team…</p>
          ) : (
            <ul className={styles.list}>
              {members.map((member) => {
                const id = member._id || member.id;
                const isMe = id === myId;
                const role = member.role || (member.isAdmin ? "admin" : null);
                return (
                  <li key={id} className={styles.member}>
                    <div className={styles.memberInfo}>
                      <span className={styles.memberName}>
                        {member.name} {isMe && <span className={styles.you}>(you)</span>}
                      </span>
                      <span className={styles.memberEmail}>{member.email}</span>
                    </div>
                    <span className={roleChipClass(role)}>{role || "no access"}</span>
                    <div className={styles.actions}>
                      {!isMe && (
                        <>
                          <select
                            className={styles.select}
                            value={role || ""}
                            onChange={(e) => handleRoleChange(member, e.target.value)}
                          >
                            {!role && <option value="">no access</option>}
                            {STAFF_ROLES.map((r) => (
                              <option key={r} value={r}>
                                {r}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className={styles.iconBtn}
                            onClick={() => handleResetPassword(member)}
                          >
                            Reset password
                          </button>
                          <button
                            type="button"
                            className={styles.dangerBtn}
                            onClick={() => handleDelete(member)}
                          >
                            Remove
                          </button>
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Add a team member</h2>
          <form onSubmit={handleCreate}>
            <div className={styles.formGrid}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="team-name">Name</label>
                <input
                  id="team-name"
                  className={styles.input}
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="team-email">Email</label>
                <input
                  id="team-email"
                  className={styles.input}
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="team-phone">Phone</label>
                <input
                  id="team-phone"
                  className={styles.input}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="team-password">Password</label>
                <input
                  id="team-password"
                  className={styles.input}
                  type="password"
                  required
                  minLength={8}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="team-role">Role</label>
                <select
                  id="team-role"
                  className={styles.select}
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                >
                  {STAFF_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
                <p className={styles.roleHint}>{ROLE_DESCRIPTIONS[form.role]}</p>
              </div>
            </div>
            <button className={styles.submit} type="submit" disabled={saving}>
              {saving ? "Adding…" : "Add member"}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
};

export default Team;
